import type { HabitMeasure } from '@goaltree/backend/convex/lib/habitMeasure'
import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'

// A durable FIFO of mutations made while offline. Replayed against Convex when
// connectivity returns. Kept intentionally small — only the operations that are
// safe to apply blind, in order, without server-side conflict resolution.

export type TaskStatus = 'backlog' | 'in-progress' | 'done'

export type CreateTaskArgs = {
  tempId: string
  parentId: string
  title: string
  description?: string
  tags?: string[]
  effort?: 'low' | 'medium' | 'high'
  recursPeriod?: string
  recursDays?: number[]
  recurrenceTime?: string
  recurrenceTzOffset?: number
  recurrenceDay?: number
  durationMinutes?: number
  dueDate?: number
  isHabit?: boolean
  category?: string
  measure?: HabitMeasure
  icon?: string
}

export type OfflineOp =
  | { id: string; kind: 'complete'; createdAt: number; args: { taskId: string } }
  | { id: string; kind: 'uncomplete'; createdAt: number; args: { taskId: string } }
  | {
      id: string
      kind: 'setTaskStatus'
      createdAt: number
      args: { taskId: string; status: TaskStatus }
    }
  | {
      id: string
      kind: 'setIsHabit'
      createdAt: number
      args: { taskId: string; isHabit: boolean }
    }
  | {
      id: string
      kind: 'setEffort'
      createdAt: number
      args: { taskId: string; effort: 'low' | 'medium' | 'high'; superUrgent?: boolean }
    }
  | {
      id: string
      kind: 'skipToday'
      createdAt: number
      args: { taskId: string; localDate?: string; reason?: string }
    }
  | {
      id: string
      kind: 'unskipToday'
      createdAt: number
      args: { taskId: string; localDate?: string }
    }
  | {
      id: string
      kind: 'skipHabitToday'
      createdAt: number
      args: { habitId: string; localDate?: string; reason?: string }
    }
  | {
      id: string
      kind: 'unskipHabitToday'
      createdAt: number
      args: { habitId: string; localDate?: string }
    }
  | {
      id: string
      kind: 'togglePin'
      createdAt: number
      args: { taskId: string }
    }
  | {
      id: string
      kind: 'createTask'
      createdAt: number
      // tempId is the client-generated id used for optimistic rendering until replay.
      args: CreateTaskArgs
    }

interface OfflineState {
  queue: OfflineOp[]
  enqueue: (op: Omit<OfflineOp, 'id' | 'createdAt'> & { id?: string }) => string
  remove: (id: string) => void
  clear: () => void
}

let seq = 0
function nextId() {
  seq += 1
  return `op_${seq}_${performance.now().toString(36).replace('.', '')}`
}

export const useOfflineStore = create<OfflineState>()(
  persist(
    (set) => ({
      queue: [],
      enqueue: (op) => {
        const id = op.id ?? nextId()
        const full = {
          ...op,
          id,
          createdAt: performance.timeOrigin + performance.now(),
        } as OfflineOp
        set((s) => ({ queue: [...s.queue, full] }))
        return id
      },
      remove: (id) => set((s) => ({ queue: s.queue.filter((o) => o.id !== id) })),
      clear: () => set({ queue: [] }),
    }),
    {
      name: 'offline-queue',
      storage: createJSONStorage(() => localStorage),
    },
  ),
)

export function tempTaskId() {
  return `temp:${nextId()}`
}

export function isTempId(id: string) {
  return id.startsWith('temp:')
}
